import type { TodayStatus } from '@calorie-tracker/api';

import { appScreens } from './constants';
import type { AppEntrySummary, AppScreen } from './types';

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

export function formatDateForSummary(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function formatLocalTimestamp(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  const seconds = `${date.getSeconds()}`.padStart(2, '0');
  const milliseconds = `${date.getMilliseconds()}`.padStart(3, '0');
  const offsetMinutes = date.getTimezoneOffset();
  const offsetSign = offsetMinutes <= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = `${Math.floor(absoluteOffsetMinutes / 60)}`.padStart(2, '0');
  const offsetRemainderMinutes = `${absoluteOffsetMinutes % 60}`.padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetRemainderMinutes}`;
}

function createDateFromDateString(value: string): Date {
  const [year = 1970, month = 1, day = 1] = value.split('-').map(Number);

  return new Date(year, month - 1, day);
}

export function formatDateForHeading(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateStringForHeading(value: string): string {
  return formatDateForHeading(createDateFromDateString(value));
}

export function readScreenFromLocation(): AppScreen {
  if (typeof window === 'undefined') {
    return 'today';
  }

  const hash = window.location.hash.replace(/^#/, '');

  if (hash.startsWith('log/')) {
    return 'log';
  }

  if (appScreens.some((screen) => screen.id === hash)) {
    return hash as AppScreen;
  }

  if (hash === 'goals') {
    return 'goals';
  }

  return 'log';
}

export function formatNumber(value: number): string {
  const roundedValue = Math.round(value * 10) / 10;

  return Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(1);
}

export function formatMealTypeLabel(mealType: string): string {
  return mealType.charAt(0).toUpperCase() + mealType.slice(1);
}

export function formatMealTime(value: Date | string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

export function createEditableMacroState(entry: AppEntrySummary) {
  return {
    normalizedName: entry.normalizedName,
    inputQuantityAmount: `${entry.inputQuantity.amount}`,
    inputQuantityUnit: entry.inputQuantity.unit,
    calories: `${entry.macros.calories}`,
    proteinGrams: `${entry.macros.proteinGrams}`,
    carbsGrams: `${entry.macros.carbsGrams}`,
    fatGrams: `${entry.macros.fatGrams}`,
  };
}

export function formatSourceLabel(source: string): string {
  switch (source) {
    case 'usda':
      return 'USDA';
    case 'food-label':
      return 'Label';
    case 'ai-estimate':
      return 'AI estimate';
    case 'web-search':
      return 'Web search';
    default:
      return 'Manual';
  }
}

export function formatRemainingLabel(value: number, unit: string): string {
  const absoluteValue = formatNumber(Math.abs(value));

  return value >= 0 ? `${absoluteValue}${unit} to target` : `${absoluteValue}${unit} over`;
}

export function getTodayStatusMeta(status: TodayStatus): {
  badgeClassName: string;
  description: string;
  label: string;
} {
  switch (status) {
    case 'over-target':
      return {
        badgeClassName: 'border border-amber-300 bg-amber-50 text-amber-900',
        description: 'Calories are above today’s target.',
        label: 'Over target',
      };
    case 'on-track':
      return {
        badgeClassName: 'border border-border bg-background text-foreground',
        description: 'You are close to today’s calorie target.',
        label: 'On track',
      };
    case 'no-goal':
      return {
        badgeClassName: 'border border-border bg-secondary text-secondary-foreground',
        description: 'Set a goal to see remaining calories and macros.',
        label: 'No goal',
      };
    default:
      return {
        badgeClassName: 'border border-border bg-secondary text-secondary-foreground',
        description: 'You still have room before today’s target.',
        label: 'Under target',
      };
  }
}

export function formatFileSize(value: number): string {
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }

  return `${value} B`;
}
